import { useState } from 'react';

type FormState = 'idle' | 'submitting' | 'success' | 'error';

export default function FinalCTA() {
  const [formState, setFormState] = useState<FormState>('idle');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    interest: '',
    message: '',
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formState === 'submitting') return;
    setFormState('submitting');
    const body = new URLSearchParams();
    Object.entries(formData).forEach(([k, v]) => body.append(k, v));
    try {
      const res = await fetch('https://readdy.ai/api/form/d73ahiqfp7tlt849ngpg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setFormState(res.ok ? 'success' : 'error');
      if (res.ok) setFormData({ name: '', email: '', phone: '', interest: '', message: '' });
    } catch {
      setFormState('error');
    }
  };

  return (
    <section id="consultation" className="relative py-28 px-4 md:px-8 overflow-hidden">
      {/* Dramatic background */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage:
            'url(https://public.readdy.ai/ai/img_res/45f31e834d8cf632f555410d89cf44f5.jpg)',
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-br from-charcoal/97 via-charcoal/90 to-charcoal/80" />

      {/* Gold accent lines */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/45 to-transparent" />

      {/* Decorative circles */}
      <div className="absolute top-12 right-16 w-32 h-32 rounded-full border border-gold/8 pointer-events-none" />
      <div className="absolute top-20 right-24 w-20 h-20 rounded-full border border-gold/6 pointer-events-none" />
      <div className="absolute bottom-12 left-12 w-24 h-24 rounded-full border border-gold/8 pointer-events-none" />

      <div className="relative z-10 max-w-5xl mx-auto">
        <div className="flex flex-col lg:flex-row gap-16 items-start">
          {/* Left: headline + contact */}
          <div className="lg:w-2/5">
            <p className="text-[10px] tracking-[0.55em] uppercase text-gold mb-6">Take the First Step</p>
            <h2 className="font-serif text-4xl md:text-5xl text-white font-light leading-[1.1] mb-6">
              Work With a Local{' '}
              <em className="italic text-gold">Expert</em>{' '}
              Who Understands the Tahoe Market
            </h2>
            <div className="flex items-center gap-3 mb-7">
              <div className="w-16 h-px bg-gold/35" />
              <div className="w-1.5 h-1.5 rotate-45 bg-gold/50" />
              <div className="w-16 h-px bg-gold/35" />
            </div>
            <p className="text-warm-white/55 text-sm leading-relaxed mb-10">
              Whether you&apos;re buying your first property, selling a generational asset, or seeking your dream alpine retreat — Cam Kirkwood delivers expert guidance and a strategy tailored precisely to your goals in the Lake Tahoe and Sierra Nevada luxury market.
            </p>

            {/* Contact details */}
            <div className="space-y-4 mb-8">
              <a href="tel:5617146589" className="flex items-center gap-4 group cursor-pointer">
                <div className="w-11 h-11 flex items-center justify-center border border-gold/30 rounded-xl group-hover:bg-gold/12 transition-all duration-300">
                  <i className="ri-phone-line text-gold" />
                </div>
                <div>
                  <p className="text-[9px] tracking-[0.3em] uppercase text-warm-white/35">Call Directly</p>
                  <p className="text-warm-white text-sm font-medium">561-714-6589</p>
                  <p className="text-warm-white/30 text-xs">Fax: 561-655-2359</p>
                </div>
              </a>
              <a href="mailto:cam@camkirkwood.com" className="flex items-center gap-4 group cursor-pointer">
                <div className="w-11 h-11 flex items-center justify-center border border-gold/30 rounded-xl group-hover:bg-gold/12 transition-all duration-300">
                  <i className="ri-mail-line text-gold" />
                </div>
                <div>
                  <p className="text-[9px] tracking-[0.3em] uppercase text-warm-white/35">Email</p>
                  <p className="text-warm-white text-sm font-medium">cam@camkirkwood.com</p>
                </div>
              </a>
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 flex items-center justify-center border border-gold/30 rounded-xl">
                  <i className="ri-map-pin-line text-gold" />
                </div>
                <div>
                  <p className="text-[9px] tracking-[0.3em] uppercase text-warm-white/35">Office</p>
                  <p className="text-warm-white/65 text-xs leading-snug">
                    11000 Prosperity Farms Rd, Suite 103<br />
                    Palm Beach Gardens, FL 33410
                  </p>
                </div>
              </div>
            </div>

            {/* Trust signals */}
            <div className="pt-6 border-t border-white/8">
              <div className="flex flex-wrap gap-x-4 gap-y-2 mb-4">
                {['Compass Florida, LLC', 'Tahoe Luxury Specialist', '500+ Clients'].map((s) => (
                  <span key={s} className="text-[10px] tracking-wider text-warm-white/30 uppercase">{s}</span>
                ))}
              </div>
              <div className="flex flex-wrap gap-4">
                <a href="https://www.yelp.com/biz/cam-kirkwood-compass-jupiter" target="_blank" rel="noopener noreferrer nofollow" className="text-[10px] text-gold/55 hover:text-gold transition-colors cursor-pointer flex items-center gap-1">
                  <i className="ri-star-fill text-xs" /> Yelp Reviews
                </a>
                <a href="https://www.zillow.com/profile/CamKirkwood88/" target="_blank" rel="noopener noreferrer nofollow" className="text-[10px] text-gold/55 hover:text-gold transition-colors cursor-pointer flex items-center gap-1">
                  <i className="ri-home-4-line text-xs" /> Zillow
                </a>
                <a href="https://www.realtor.com/realestateagents/cam-kirkwood_palm-beach-gardens_fl_3714488" target="_blank" rel="noopener noreferrer nofollow" className="text-[10px] text-gold/55 hover:text-gold transition-colors cursor-pointer flex items-center gap-1">
                  <i className="ri-building-line text-xs" /> Realtor.com
                </a>
              </div>
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:w-3/5 w-full">
            <div
              className="rounded-2xl p-8 md:p-10"
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.09)',
                backdropFilter: 'blur(12px)',
              }}
            >
              <div className="mb-8">
                <p className="font-serif text-2xl md:text-3xl text-white font-light">
                  Request a Private Consultation
                </p>
                <p className="text-warm-white/40 text-xs mt-2 tracking-wide">
                  Confidential &bull; No obligation &bull; Response within 24 hours
                </p>
              </div>

              {formState === 'success' ? (
                <div className="text-center py-14">
                  <div className="w-14 h-14 flex items-center justify-center bg-gold/15 rounded-full mx-auto mb-5 border border-gold/35">
                    <i className="ri-check-line text-gold text-2xl" />
                  </div>
                  <p className="text-white font-serif text-2xl mb-3">Thank You</p>
                  <p className="text-warm-white/50 text-sm leading-relaxed max-w-xs mx-auto">
                    Cam will be in touch shortly to schedule your private consultation.
                  </p>
                </div>
              ) : (
                <form data-readdy-form id="consultation-form" onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] tracking-[0.25em] uppercase text-warm-white/40 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Your full name"
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-white text-sm placeholder-warm-white/20 focus:outline-none focus:border-gold/40 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] tracking-[0.25em] uppercase text-warm-white/40 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="your@email.com"
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-white text-sm placeholder-warm-white/20 focus:outline-none focus:border-gold/40 transition-colors"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] tracking-[0.25em] uppercase text-warm-white/40 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="(555) 000-0000"
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-white text-sm placeholder-warm-white/20 focus:outline-none focus:border-gold/40 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] tracking-[0.25em] uppercase text-warm-white/40 mb-2">
                        I&apos;m Interested In
                      </label>
                      <select
                        name="interest"
                        value={formData.interest}
                        onChange={handleChange}
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-white text-sm focus:outline-none focus:border-gold/40 transition-colors appearance-none cursor-pointer"
                      >
                        <option value="" className="text-charcoal bg-white">Select…</option>
                        <option value="Buying a Luxury Home" className="text-charcoal bg-white">Buying a Luxury Home</option>
                        <option value="Selling a Property" className="text-charcoal bg-white">Selling a Property</option>
                        <option value="Vacation / Second Home" className="text-charcoal bg-white">Vacation / Second Home</option>
                        <option value="Investment Property" className="text-charcoal bg-white">Investment Property</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-[10px] tracking-[0.25em] uppercase text-warm-white/40 mb-2">
                      Message
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      maxLength={500}
                      placeholder="Tell Cam about your real estate goals in Lake Tahoe or the Sierra Nevada…"
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-white text-sm placeholder-warm-white/20 focus:outline-none focus:border-gold/40 transition-colors resize-none"
                    />
                    <p className="text-right text-warm-white/20 text-xs mt-1">{formData.message.length}/500</p>
                  </div>
                  {formState === 'error' && (
                    <p className="text-red-400 text-xs">
                      Something went wrong. Please try again or call 561-714-6589 directly.
                    </p>
                  )}
                  <button
                    type="submit"
                    disabled={formState === 'submitting'}
                    className="w-full py-5 bg-gold text-white text-[11px] tracking-[0.28em] uppercase font-medium cursor-pointer rounded-xl hover:bg-[#b8983a] transition-all duration-300 disabled:opacity-60 whitespace-nowrap"
                    style={{ boxShadow: '0 0 30px rgba(196,169,98,0.25)' }}
                  >
                    {formState === 'submitting' ? 'Sending…' : 'Start Your Real Estate Consultation'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
